import {
  ChangeEventHandler,
  FC,
  memo,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { FaPlus } from 'react-icons/fa';
import { CiSearch } from 'react-icons/ci';

import Image from 'next/image';

import { useTheme } from 'styled-components';

import { lightDynamicColors } from '~/common/styles/theme';
import { useAuth } from '~/common/providers/auth';
import { useMediaQuery } from '~/common/hooks/use-media-query';
import { Typography } from '~/common/components/Typograph';
import { TextInput } from '~/common/components/TextInput';
import { CircularGlow } from '~/common/components/CircularGlow';
import { Button } from '~/common/components/Button';

import {
  AdminActionsContainer,
  ArcImageCard,
  ArcTrack,
  Container,
  HeroImageArc,
  HeroTitle,
  HighlightDiagonal,
} from './styles';

const images = [
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/58fa2ebc-8a17-4c08-8bbc-2864f18fee56.png',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/7dcfc064-29b3-453f-afab-a6bbff5cf10a.png',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/a7898659-0621-48f2-86f2-3e22a4846622.png',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/028dde9b-a298-4ce5-bc7e-71ba16127001.jpg',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/d96a459f-ba5c-4633-8dd6-7af21eff13c8.png',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/e3b7a586-7b0d-41b4-84cd-75de21cf8cb9.png',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/c6b9fd0a-8054-424e-8378-ec4ca19cf2f1.png',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/0345bfbd-f9a1-4bfc-aafd-56a52a965120.jpg',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/79f46001-8c62-4177-931c-71d805590912.png',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/1f5c53ca-eb9a-4754-8b9b-0e78a127d18e.png',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/a0c03b9d-05c9-48c9-8ee4-72eec7dbeaf4.png',
  'https://prompts-mestes-public.s3.sa-east-1.amazonaws.com/prompt/media/61bb7ef2-e704-4a2f-85a8-2fecd2b0e6cc.png',
];

// Toggle simples para remover/reativar a animacao de entrada.
const ENABLE_SHUFFLE_ENTRY_ANIMATION = true;

interface IPromptListTitleProps {
  handleNewPrompt: () => void;
  handleSearchChange: ChangeEventHandler<HTMLInputElement>;
  initialSearch?: string;
}
export const PromptListHeader: FC<IPromptListTitleProps> = memo(
  ({ handleNewPrompt, handleSearchChange, initialSearch }) => {
    const {
      loggedIn,
      user: { type: userType = '', canCreatePrompts = false },
    } = useAuth();

    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width: 600px)');
    const searchInputRef = useRef<HTMLInputElement>(null);

    const isAdmin = useMemo(
      () => loggedIn && userType.toLowerCase() === 'admin',
      [loggedIn, userType]
    );

    const renderCreatePromptButton = useMemo(
      () => isAdmin || canCreatePrompts,
      [isAdmin, canCreatePrompts]
    );

    const arcItems = useMemo(() => {
      const startAngle = -70;
      const endAngle = 70;
      const total = Math.max(images.length, 2);
      const step = (endAngle - startAngle) / (total - 1);

      return images.map((src, index) => {
        const progress = total === 1 ? 0 : index / (total - 1);
        const angle = startAngle + step * index;
        const tilt = -16 + progress * 32;
        const direction = index % 2 === 0 ? -1 : 1;
        const shuffleX = direction * (24 + (index % 4) * 8);
        const shuffleY = 18 + (index % 5) * 6;
        const shuffleRotate = direction * (8 + (index % 3) * 5);

        return {
          src,
          angle,
          tilt,
          delay: index * 45,
          shuffleX,
          shuffleY,
          shuffleRotate,
        };
      });
    }, []);

    useEffect(() => {
      if (isMobile) {
        return undefined;
      }

      const focusTimer = window.setTimeout(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      }, 220);

      const blurTimer = window.setTimeout(() => {
        const input = searchInputRef.current;

        if (
          input &&
          document.activeElement === input &&
          input.value.trim().length === 0
        ) {
          input.blur();
        }
      }, 1200);

      return () => {
        window.clearTimeout(focusTimer);
        window.clearTimeout(blurTimer);
      };
    }, [isMobile]);

    return (
      <Container>
        <HeroTitle>
          <HeroImageArc>
            <ArcTrack>
              {arcItems.map(
                (
                  {
                    src,
                    angle,
                    tilt,
                    delay,
                    shuffleX,
                    shuffleY,
                    shuffleRotate,
                  },
                  index
                ) => (
                  <ArcImageCard
                    key={`${src}-${angle}`}
                    $angle={angle}
                    $tilt={tilt}
                    $delay={delay}
                    $shuffleX={shuffleX}
                    $shuffleY={shuffleY}
                    $shuffleRotate={shuffleRotate}
                    $enableShuffleEntry={ENABLE_SHUFFLE_ENTRY_ANIMATION}
                  >
                    <Image
                      src={src}
                      alt={`Prompt ${index + 1}`}
                      fill
                      quality={75}
                      sizes="(max-width: 600px) 62px, (max-width: 1024px) 80px, 124px"
                      draggable={false}
                    />
                  </ArcImageCard>
                )
              )}
            </ArcTrack>
          </HeroImageArc>

          <CircularGlow
            opacity={0.3}
            animationBreathingTime="10s"
            width="260px"
            height="260px"
          />

          <Typography.Title
            size="h4"
            color="neutral0"
            style={{ fontWeight: 'bold' }}
            sora
          >
            O universo de{' '}
            <HighlightDiagonal>
              <span>prompts</span>
            </HighlightDiagonal>{' '}
            que movem a nova era de criação com IA
          </Typography.Title>
        </HeroTitle>

        {renderCreatePromptButton && (
          <AdminActionsContainer>
            {renderCreatePromptButton && (
              <Button
                icon={FaPlus}
                text={isMobile ? 'Prompt' : 'Novo Prompt'}
                backgroundColor={theme.primary}
                textOrIconColor={theme.neutral1000}
                borderColor={theme.transparent}
                hoverBackgroundColor={lightDynamicColors.primary}
                onClick={handleNewPrompt}
                justifyContent="center"
              />
            )}
          </AdminActionsContainer>
        )}

        <TextInput
          ref={searchInputRef}
          withBorderEffect
          placeholder="Procure por prompts..."
          width="100%"
          leftIcon={CiSearch}
          onChange={handleSearchChange}
          defaultValue={initialSearch}
          backgroundColor="neutral950"
          onHoverBackgroundColor="neutral900"
          onFocusBorderColor="neutral700"
          borderColor="neutral700"
        />
      </Container>
    );
  },
  (prev, next) =>
    prev.handleNewPrompt === next.handleNewPrompt &&
    prev.handleSearchChange === next.handleSearchChange &&
    prev.initialSearch === next.initialSearch
);
